import { useState, useEffect, useMemo } from "react";
import { getPayrollDataById } from "@/hooks/payrollDataService";
import { getTotalRooms } from "@/lib/rooms";
import { Deal } from "@/types/deal";
import { toFraction } from "../finance/units";

// ─── Department → P&L category mapping ───────────────────────────────────────
const DEPT_TO_PL: Record<string, keyof PayrollByDept> = {
  "0d04c044-87bd-4d24-9007-f0628ef6ee3a": "rooms", // Front Office
  "e156800b-55e8-4b3f-a5f9-64b8fe266512": "rooms", // Housekeeping
  "d93b774f-3906-47e9-ba2f-90675588f31c": "fnb", // F&B
  "559c7da5-01f0-4a2d-9764-ad7d6fb70220": "wellness", // Spa/Wellness
  "c5e5738e-0b31-4940-9b10-8ca654b9917f": "maintenance", // Maintenance
  "508d5c3b-c493-43f9-a796-11558a7a1fc9": "ag", // A&G
  "8607eb3b-2103-4615-b96c-9eef9f282afa": "sales", // Sales & Marketing ✅
};

export type PayrollByDept = {
  rooms: number;
  fnb: number;
  wellness: number;
  ag: number;
  sales: number;
  maintenance: number;
};

export function computePayrollByDept(
  positions: any[],
  socialChargesPercent: number,
): PayrollByDept {
  const result: PayrollByDept = {
    rooms: 0,
    fnb: 0,
    wellness: 0,
    ag: 0,
    sales: 0,
    maintenance: 0,
  };
  const mult = 1 + socialChargesPercent / 100;
  for (const pos of positions) {
    if (!pos.included) continue;
    const monthly = pos.overrideSalary ?? pos.monthlySalary ?? 0;
    const ftes = pos.overrideFTEs ?? pos.calculatedFTEs ?? 0;
    const annual = monthly * ftes * 12 * mult;
    const key = DEPT_TO_PL[pos.departmentId] ?? "ag";
    result[key] += annual;
  }
  return result;
}

// ─── Types ────────────────────────────────────────────────────────────────────
export type PLYearData = {
  year: number;
  total: number;
  pctOfTR?: number;
  por?: number;
  par?: number;
};

export type PLRow = {
  id: string;
  label: string;
  group: "KPIS" | "REVENUE" | "DIRECT" | "UNDISTRIBUTED" | "FIXED" | "SUMMARY";
  type: "kpi" | "line" | "subtotal" | "total" | "section";
  years: PLYearData[];
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getExitYear(deal: Deal): number | null {
  const e = deal.kpis?.exit;
  if (!e) return null;
  if (e.strategy === "SALE" && e.sale?.exitYear) return e.sale.exitYear;
  if (e.strategy === "REFINANCE" && e.refinance?.refinanceYear)
    return e.refinance.refinanceYear;
  return e.holdPeriodYears ?? null;
}

function getOpex(deal: Deal, key: string): number {
  return deal.kpis?.opex?.[key] ?? 0;
}

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

// ─── applyPLHorizon ───────────────────────────────────────────────────────────
export function applyPLHorizon(
  rows: PLRow[],
  horizon: number,
  totalRevenueByYear: number[],
  roomsSoldByYear: number[],
  roomsCountByYear: number[],
): PLRow[] {
  const H = Math.max(0, Math.min(10, horizon ?? 10));
  return rows.map((r) => ({
    ...r,
    years: (r.years ?? []).map((cell, idx) => {
      const yearNo = idx + 1;
      if (yearNo > H)
        return { year: yearNo, total: 0, pctOfTR: 0, por: 0, par: 0 };
      const total = cell?.total ?? 0;
      const tr = totalRevenueByYear[idx] ?? 0;
      const rs = roomsSoldByYear[idx] ?? 0;
      const rc = roomsCountByYear[idx] ?? 0;
      return {
        year: yearNo,
        total,
        pctOfTR: tr > 0 ? (total / tr) * 100 : 0,
        por: rs > 0 ? total / rs : 0,
        par: rc > 0 ? total / rc : 0,
      };
    }),
  }));
}

// ─── getFallbackPayroll ───────────────────────────────────────────────────────
function getFallbackPayroll(deal: Deal): PayrollByDept {
  const total = deal.kpis?.payroll?.totalAnnual || 0;
  return {
    rooms: total * 0.236,
    fnb: total * 0.346,
    wellness: total * 0.11,
    ag: total * 0.152,
    sales: total * 0.073,
    maintenance: total * 0.083,
  };
}

// ─── calculatePLData ──────────────────────────────────────────────────────────
export function calculatePLData(
  deal: Deal,
  yearCount: number = 10,
  externalPayrollByDept: PayrollByDept | null = null,
): PLRow[] {
  if (!deal?.kpis) return [];

  const safeYearCount = Math.max(1, Math.min(10, yearCount));
  const yearArray = Array.from({ length: safeYearCount }, (_, i) => i + 1);
  const horizon = getExitYear(deal) ?? safeYearCount;

  // ── Rooms count ────────────────────────────────────────────────────────────
  const rooms = Math.max(
    1,
    (Array.isArray(deal.kpis?.roomMix)
      ? deal.kpis.roomMix.reduce(
          (s: number, r: any) => s + (Number(r.rooms) || Number(r.count) || 0),
          0,
        )
      : 0) ||
      deal.kpis?.rooms?.total ||
      deal.kpis?.roomsTotal ||
      getTotalRooms(deal) ||
      1,
  );

  // ── Macro indices ──────────────────────────────────────────────────────────
  const ramp = deal.kpis?.rampSettings;
  const revenueRampArr = ramp?.revenueRamp || [0.85, 1.0, 1.0, 1.0];
  const costRampArr = ramp?.costRamp || [1.15, 1.1, 1.0, 1.0];
  const toplineRate = (ramp?.toplineGrowthPct || 3) / 100;
  const inflationRate = (ramp?.inflationPct || 5) / 100;
  const depPct = (ramp?.depreciationPctOfCapex || 3) / 100;

  const rr = (n: number) => (n >= 1 && n <= 4 ? revenueRampArr[n - 1] : 1.0);
  const cr = (n: number) => (n >= 1 && n <= 4 ? costRampArr[n - 1] : 1.0);
  const ti = (n: number) => Math.pow(1 + toplineRate, n);
  const ii = (n: number) => Math.pow(1 + inflationRate, n);

  // ── Room Revenue ───────────────────────────────────────────────────────────
  const rvt =
    deal.kpis?.roomRevenue?.totals ||
    (() => {
      const adrA = deal.kpis?.roomRev?.adr || [];
      const occA = deal.kpis?.roomRev?.occ || [];
      if (!adrA.length) return null;
      const avgADR =
        adrA.reduce((s: number, v: number) => s + v, 0) / adrA.length;
      const avgOcc =
        occA.reduce((s: number, v: number) => s + v, 0) / occA.length;
      const avail = rooms * 365;
      const sold = avail * (avgOcc / 100);
      return {
        avgADR,
        avgOccPct: avgOcc,
        roomsAvailable: avail,
        roomsSold: sold,
        roomsRevenue: avgADR * sold,
      };
    })();

  const baseADR = rvt?.avgADR || deal.kpis?.avgADR || 0;
  const baseOcc = clamp01(
    toFraction(rvt?.avgOccPct || deal.kpis?.avgOccupancy || 0),
  );

  const availArr: number[] = [];
  const adrArr: number[] = [];
  const occArr: number[] = [];
  const soldArr: number[] = [];
  const revparArr: number[] = [];
  const roomsRevArr: number[] = [];

  yearArray.forEach((n) => {
    const RA = rooms * 365;
    const ADR = baseADR * rr(n) * ti(n);
    const OCC = clamp01(baseOcc * rr(n));
    availArr.push(RA);
    adrArr.push(ADR);
    occArr.push(OCC * 100);
    soldArr.push(RA * OCC);
    revparArr.push(ADR * OCC);
    roomsRevArr.push(ADR * RA * OCC);
  });

  // ── F&B Revenue ───────────────────────────────────────────────────────────
  const fnb = deal.kpis?.fnb;
  const avgGuests = fnb?.avgGuests ?? 2.8;
  const capture = fnb?.capture ?? {};
  const guestCheck = fnb?.check?.guest ?? {};
  const extCheck = fnb?.check?.external ?? {};
  const extPerDay = fnb?.external?.perDay ?? {};

  const meals = ["breakfast", "lunch", "dinner", "bar"] as const;

  const fnbRevArr = yearArray.map((n) => {
    const guests = avgGuests * soldArr[n - 1];
    let total = 0;
    for (const meal of meals) {
      const cap = Number(capture[meal] ?? 0) / 100;
      const cg = Number(guestCheck[meal] ?? 0);
      const ep = Number(extPerDay[meal] ?? 0);
      const ce = Number(extCheck[meal] ?? 0);
      const captureN = cap * rr(n);
      const checkGN = cg * rr(n) * ti(n);
      const extDayN = ep * rr(n);
      const extChkN = ce * rr(n) * ti(n);
      total += guests * captureN * checkGN + extDayN * 365 * extChkN;
    }
    return total;
  });

  // ── Spa Revenue ───────────────────────────────────────────────────────────
  const treatBase = deal.kpis?.otherRev?.wellness?.treatmentsPerDay ?? 4;
  const priceBase = deal.kpis?.otherRev?.wellness?.avgPrice ?? 1200;

  const spaRevArr = yearArray.map((n) => {
    const treatN = treatBase * rr(n);
    const priceN = priceBase * rr(n) * ti(n);
    return treatN * 365 * priceN;
  });

  // ── Other Revenue ─────────────────────────────────────────────────────────
  // FIX: mode can be "pct", "pctRooms" (% of rooms revenue), or "fixed"
  const otherMode = deal.kpis?.otherRev?.other?.mode;
  const otherValue = deal.kpis?.otherRev?.other?.value ?? 0;

  // "pct" and "pctRooms" both = % of rooms revenue (matching Excel formula)
  const otherPct =
    otherMode === "pct" || otherMode === "pctRooms" ? otherValue / 100 : 0;
  const otherFixed = otherMode === "fixed" ? otherValue : 0;

  const otherRevArr = yearArray.map((n) => {
    const pctN = otherPct * rr(n);
    const fixedN = otherFixed * rr(n) * ti(n);
    return pctN * roomsRevArr[n - 1] + fixedN * 12;
  });

  const totalRevArr = yearArray.map(
    (_, i) => roomsRevArr[i] + fnbRevArr[i] + spaRevArr[i] + otherRevArr[i],
  );

  // ── Payroll ───────────────────────────────────────────────────────────────
  const stabPayroll = externalPayrollByDept ?? getFallbackPayroll(deal);

  type DeptKey = keyof PayrollByDept;
  const pr: Record<DeptKey, number[]> = {
    rooms: [],
    fnb: [],
    wellness: [],
    ag: [],
    sales: [],
    maintenance: [],
  };
  (Object.keys(pr) as DeptKey[]).forEach((dept) => {
    const base = stabPayroll[dept];
    pr[dept] = yearArray.map((n) => base * cr(n) * ii(n));
  });

  // ── Operating Costs ────────────────────────────────────────────────────────
  const opex = {
    roomsCommission: [] as number[],
    guestSupplies: [] as number[],
    cogs: [] as number[],
    meCosts: [] as number[],
    wellnessOther: [] as number[],
    otherDirect: [] as number[],
    otherAG: [] as number[],
    techSubscriptions: [] as number[],
    otherSM: [] as number[],
    maintenanceOther: [] as number[],
    utilities: [] as number[],
    managementFees: [] as number[],
    propertyTaxes: [] as number[],
    insurance: [] as number[],
  };

  yearArray.forEach((n, i) => {
    const C = cr(n);
    const I = ii(n);
    const TR = totalRevArr[i];
    const RS = soldArr[i];
    const RR = roomsRevArr[i];
    const FB = fnbRevArr[i];
    const OR = otherRevArr[i];

    opex.roomsCommission[i] = (getOpex(deal, "roomsCommission") / 100) * C * RR;
    opex.cogs[i] = (getOpex(deal, "cogs") / 100) * C * FB;
    // M&E costs = % of Other Operating Revenue
    opex.meCosts[i] = (getOpex(deal, "meetingsEvents") / 100) * C * OR;

    opex.guestSupplies[i] = getOpex(deal, "guestSuppliesCleaning") * C * I * RS;

    opex.wellnessOther[i] = getOpex(deal, "wellnessOther") * 12 * C * I;
    opex.otherDirect[i] = getOpex(deal, "otherDirect") * 12 * C * I;

    opex.otherAG[i] = (getOpex(deal, "adminGeneralOther") / 100) * C * TR;
    opex.otherSM[i] = (getOpex(deal, "salesMarketingOther") / 100) * C * TR;
    opex.maintenanceOther[i] =
      (getOpex(deal, "maintenanceOther") / 100) * C * TR;
    opex.utilities[i] = (getOpex(deal, "utilities") / 100) * C * TR;

    opex.techSubscriptions[i] = getOpex(deal, "techSubscriptions") * 12 * C * I;

    opex.managementFees[i] = (getOpex(deal, "mgmtFee") / 100) * C * TR;
    opex.propertyTaxes[i] = (getOpex(deal, "propertyTaxes") / 100) * C * TR;
    opex.insurance[i] = (getOpex(deal, "insurance") / 100) * C * TR;
  });

  // Rent — only inflationIdx, no costRamp
  const rentArr = yearArray.map((n) => getOpex(deal, "rent") * 12 * ii(n));

  // ── Financing ──────────────────────────────────────────────────────────────
  const capex =
    deal.kpis?.investmentBudget?.grandTotal ||
    deal.kpis?.budget?.grandTotal ||
    0;

  const annualDepreciation = depPct * capex;

  const fin = deal.kpis?.financing;

  if (process.env.NODE_ENV === "development") {
    console.log(
      "[plCalculations] deal.kpis.financing =",
      JSON.stringify(fin, null, 2),
    );
  }

  const ltcFraction =
    (fin?.ltcPct != null ? fin.ltcPct / 100 : null) ??
    (fin?.ltcPercent != null ? fin.ltcPercent / 100 : null) ??
    (fin?.ltc != null ? fin.ltc / 100 : null) ??
    (fin?.loanToValue != null ? fin.loanToValue / 100 : null) ??
    (fin?.loanToCost != null ? fin.loanToCost / 100 : null) ??
    (fin?.ltcRatio != null ? fin.ltcRatio : null) ??
    0;

  const interestRate =
    (fin?.interestRatePct != null ? fin.interestRatePct / 100 : null) ??
    (fin?.rate != null ? fin.rate / 100 : null) ??
    (fin?.annualRate != null ? fin.annualRate / 100 : null) ??
    (fin?.interestRate != null ? fin.interestRate / 100 : null) ??
    0;

  const amortYears =
    fin?.amortizationYears ?? fin?.amortYears ?? fin?.amortization ?? 10;

  const loanAmount = ltcFraction * capex;
  const annualPrincipal = amortYears > 0 ? loanAmount / amortYears : 0;

  const exitYr = horizon;
  const interestByYear: number[] = yearArray.map((n) => {
    if (n > exitYr) return 0;
    const bop = loanAmount - annualPrincipal * (n - 1);
    return Math.max(0, bop) * interestRate;
  });

  // ── Subtotals ──────────────────────────────────────────────────────────────
  const roomsDC = yearArray.map(
    (_, i) => pr.rooms[i] + opex.roomsCommission[i] + opex.guestSupplies[i],
  );
  const fnbDC = yearArray.map((_, i) => pr.fnb[i] + opex.cogs[i]);
  const meDC = yearArray.map((_, i) => opex.meCosts[i]);
  const wellDC = yearArray.map(
    (_, i) => pr.wellness[i] + opex.wellnessOther[i],
  );
  const directT = yearArray.map(
    (_, i) => roomsDC[i] + fnbDC[i] + meDC[i] + wellDC[i] + opex.otherDirect[i],
  );
  const goi = yearArray.map((_, i) => totalRevArr[i] - directT[i]);
  const agT = yearArray.map((_, i) => pr.ag[i] + opex.otherAG[i]);
  const itT = yearArray.map((_, i) => opex.techSubscriptions[i]);
  const smT = yearArray.map((_, i) => pr.sales[i] + opex.otherSM[i]);
  const pomT = yearArray.map(
    (_, i) => pr.maintenance[i] + opex.maintenanceOther[i],
  );
  const indirectT = yearArray.map(
    (_, i) => agT[i] + itT[i] + smT[i] + pomT[i] + opex.utilities[i],
  );
  const gop = yearArray.map((_, i) => goi[i] - indirectT[i]);
  const fixedT = yearArray.map(
    (_, i) =>
      opex.managementFees[i] + opex.propertyTaxes[i] + opex.insurance[i],
  );
  const ebitdar = yearArray.map((_, i) => gop[i] - fixedT[i]);
  const ebitda = yearArray.map((_, i) => ebitdar[i] - rentArr[i]);
  const netIncome = yearArray.map(
    (_, i) => ebitda[i] - annualDepreciation - interestByYear[i],
  );

  const roomsCnt = yearArray.map(() => rooms);

  const yd = (vals: number[]): PLYearData[] =>
    vals.map((v, i) => ({
      year: i + 1,
      total: v,
      pctOfTR: totalRevArr[i] > 0 ? (v / totalRevArr[i]) * 100 : 0,
      por: soldArr[i] > 0 ? v / soldArr[i] : 0,
      par: roomsCnt[i] > 0 ? v / roomsCnt[i] : 0,
    }));

  const rows: PLRow[] = [
    {
      id: "rooms-open",
      label: "Rooms Open",
      group: "KPIS",
      type: "kpi",
      years: yearArray.map((y, i) => ({ year: y, total: roomsCnt[i] })),
    },
    {
      id: "rooms-available",
      label: "Rooms Available",
      group: "KPIS",
      type: "kpi",
      years: yd(availArr),
    },
    {
      id: "rooms-sold",
      label: "Rooms Sold",
      group: "KPIS",
      type: "kpi",
      years: yd(soldArr),
    },
    { id: "adr", label: "ADR", group: "KPIS", type: "kpi", years: yd(adrArr) },
    {
      id: "occupancy",
      label: "Occupancy",
      group: "KPIS",
      type: "kpi",
      years: yd(occArr),
    },
    {
      id: "revpar",
      label: "RevPAR",
      group: "KPIS",
      type: "kpi",
      years: yd(revparArr),
    },

    {
      id: "rooms-revenue",
      label: "Rooms Revenue",
      group: "REVENUE",
      type: "line",
      years: yd(roomsRevArr),
    },
    {
      id: "fnb-revenue",
      label: "F&B Revenue",
      group: "REVENUE",
      type: "line",
      years: yd(fnbRevArr),
    },
    {
      id: "spa-revenue",
      label: "Spa Revenue",
      group: "REVENUE",
      type: "line",
      years: yd(spaRevArr),
    },
    {
      id: "other-operating-revenue",
      label: "Other Operating Revenue",
      group: "REVENUE",
      type: "line",
      years: yd(otherRevArr),
    },
    {
      id: "total-revenue",
      label: "Total Revenue",
      group: "REVENUE",
      type: "total",
      years: yd(totalRevArr),
    },

    {
      id: "rooms-direct-payroll",
      label: "Rooms Direct Payroll",
      group: "DIRECT",
      type: "line",
      years: yd(pr.rooms),
    },
    {
      id: "rooms-commission",
      label: "Rooms Commission",
      group: "DIRECT",
      type: "line",
      years: yd(opex.roomsCommission),
    },
    {
      id: "guest-supplies-cleaning",
      label: "Guest Supplies & Cleaning",
      group: "DIRECT",
      type: "line",
      years: yd(opex.guestSupplies),
    },
    {
      id: "rooms-direct-costs",
      label: "Rooms Direct Costs",
      group: "DIRECT",
      type: "subtotal",
      years: yd(roomsDC),
    },
    {
      id: "fnb-direct-payroll",
      label: "F&B Direct Payroll",
      group: "DIRECT",
      type: "line",
      years: yd(pr.fnb),
    },
    {
      id: "cost-of-goods-sold",
      label: "Cost of Goods Sold",
      group: "DIRECT",
      type: "line",
      years: yd(opex.cogs),
    },
    {
      id: "fnb-direct-costs",
      label: "F&B Direct Costs",
      group: "DIRECT",
      type: "subtotal",
      years: yd(fnbDC),
    },
    {
      id: "me-costs",
      label: "M&E Costs",
      group: "DIRECT",
      type: "line",
      years: yd(opex.meCosts),
    },
    {
      id: "me-direct-costs",
      label: "M&E Direct Costs",
      group: "DIRECT",
      type: "subtotal",
      years: yd(meDC),
    },
    {
      id: "wellness-direct-payroll",
      label: "Wellness Direct Payroll",
      group: "DIRECT",
      type: "line",
      years: yd(pr.wellness),
    },
    {
      id: "wellness-other-costs",
      label: "Wellness Other Costs",
      group: "DIRECT",
      type: "line",
      years: yd(opex.wellnessOther),
    },
    {
      id: "wellness-direct-costs",
      label: "Wellness Direct Costs",
      group: "DIRECT",
      type: "subtotal",
      years: yd(wellDC),
    },
    {
      id: "other-direct-costs",
      label: "Other Direct Costs",
      group: "DIRECT",
      type: "line",
      years: yd(opex.otherDirect),
    },
    {
      id: "direct-costs-total",
      label: "Direct Costs",
      group: "DIRECT",
      type: "subtotal",
      years: yd(directT),
    },
    { id: "goi", label: "GOI", group: "DIRECT", type: "total", years: yd(goi) },

    {
      id: "ag-payroll",
      label: "A&G Payroll",
      group: "UNDISTRIBUTED",
      type: "line",
      years: yd(pr.ag),
    },
    {
      id: "other-ag",
      label: "Other A&G",
      group: "UNDISTRIBUTED",
      type: "line",
      years: yd(opex.otherAG),
    },
    {
      id: "admin-general",
      label: "Administrative & General",
      group: "UNDISTRIBUTED",
      type: "subtotal",
      years: yd(agT),
    },
    {
      id: "tech-subscriptions",
      label: "Tech Subscriptions",
      group: "UNDISTRIBUTED",
      type: "line",
      years: yd(opex.techSubscriptions),
    },
    {
      id: "it-telecommunications",
      label: "Information & Telecommunications",
      group: "UNDISTRIBUTED",
      type: "subtotal",
      years: yd(itT),
    },
    {
      id: "sales-marketing-payroll",
      label: "Sales & Marketing Payroll",
      group: "UNDISTRIBUTED",
      type: "line",
      years: yd(pr.sales),
    },
    {
      id: "other-sm",
      label: "Other S&M",
      group: "UNDISTRIBUTED",
      type: "line",
      years: yd(opex.otherSM),
    },
    {
      id: "sales-marketing",
      label: "Sales & Marketing",
      group: "UNDISTRIBUTED",
      type: "subtotal",
      years: yd(smT),
    },
    {
      id: "maintenance-payroll",
      label: "Maintenance Payroll",
      group: "UNDISTRIBUTED",
      type: "line",
      years: yd(pr.maintenance),
    },
    {
      id: "maintenance-other",
      label: "Maintenance Other",
      group: "UNDISTRIBUTED",
      type: "line",
      years: yd(opex.maintenanceOther),
    },
    {
      id: "property-operations-maintenance",
      label: "Property Operations & Maintenance",
      group: "UNDISTRIBUTED",
      type: "subtotal",
      years: yd(pomT),
    },
    {
      id: "utilities",
      label: "Utilities",
      group: "UNDISTRIBUTED",
      type: "line",
      years: yd(opex.utilities),
    },
    {
      id: "undistributed-total",
      label: "Indirect Costs",
      group: "UNDISTRIBUTED",
      type: "subtotal",
      years: yd(indirectT),
    },
    {
      id: "gop",
      label: "GOP",
      group: "UNDISTRIBUTED",
      type: "total",
      years: yd(gop),
    },

    {
      id: "management-fees",
      label: "Management Fees",
      group: "FIXED",
      type: "line",
      years: yd(opex.managementFees),
    },
    {
      id: "property-taxes",
      label: "Property Taxes",
      group: "FIXED",
      type: "line",
      years: yd(opex.propertyTaxes),
    },
    {
      id: "insurance",
      label: "Insurance",
      group: "FIXED",
      type: "line",
      years: yd(opex.insurance),
    },
    {
      id: "ebitdar",
      label: "EBITDAR",
      group: "FIXED",
      type: "total",
      years: yd(ebitdar),
    },
    {
      id: "rent",
      label: "Rent",
      group: "FIXED",
      type: "line",
      years: yd(rentArr),
    },
    {
      id: "ebitda",
      label: "EBITDA",
      group: "FIXED",
      type: "total",
      years: yd(ebitda),
    },

    {
      id: "depreciation",
      label: "Depreciation",
      group: "SUMMARY",
      type: "line",
      years: yd(yearArray.map(() => annualDepreciation)),
    },
    {
      id: "interest-expense",
      label: "Interest Expense",
      group: "SUMMARY",
      type: "line",
      years: yd(interestByYear),
    },
    {
      id: "net-income",
      label: "Net Income (Loss)",
      group: "SUMMARY",
      type: "total",
      years: yd(netIncome),
    },
  ];

  return applyPLHorizon(rows, horizon, totalRevArr, soldArr, roomsCnt);
}

// ─── usePLData hook ───────────────────────────────────────────────────────────
export function usePLData(deal: any | undefined, yearCount: number = 10) {
  const [payrollByDept, setPayrollByDept] = useState<PayrollByDept | null>(
    null,
  );
  const [payrollLoading, setPayrollLoading] = useState(false);

  useEffect(() => {
    const dataId = deal?.kpis?.payroll?.dataId;
    if (!dataId) return;
    setPayrollLoading(true);
    getPayrollDataById(dataId)
      .then((record) => {
        if (!record?.data?.positions) return;
        const pct = record.data.socialChargesPercent ?? 0;
        setPayrollByDept(computePayrollByDept(record.data.positions, pct));
      })
      .catch((e) => console.warn("[usePLData] payroll load failed:", e))
      .finally(() => setPayrollLoading(false));
  }, [deal?.kpis?.payroll?.dataId]);

  const exitYear = useMemo(() => {
    if (!deal) return null;
    return getExitYear(deal as Deal);
  }, [deal]);

  const plData = useMemo(() => {
    if (!deal) return [];
    const baseRows = calculatePLData(deal as Deal, yearCount, payrollByDept);
    const horizon = exitYear ?? 10;
    const roomsCount = deal.kpis?.rooms?.total || 0;
    const roomsCountByYear = Array.from(
      { length: yearCount },
      (_, i) => (deal.roomsOpenByYear?.[i] ?? roomsCount) || 0,
    );
    const totalRevenueByYear =
      baseRows
        .find((r) => r.id === "total-revenue")
        ?.years.map((y) => y.total) ?? Array(10).fill(0);
    const roomsSoldByYear =
      baseRows.find((r) => r.id === "rooms-sold")?.years.map((y) => y.total) ??
      Array(10).fill(0);
    return applyPLHorizon(
      baseRows,
      horizon,
      totalRevenueByYear,
      roomsSoldByYear,
      roomsCountByYear,
    );
  }, [deal, yearCount, payrollByDept, exitYear]);

  return {
    allRows: plData,
    kpiRows: useMemo(() => plData.filter((r) => r.group === "KPIS"), [plData]),
    operatingRows: useMemo(
      () => plData.filter((r) => r.group !== "KPIS"),
      [plData],
    ),
    exitYear,
    payrollLoading,
  };
}
